import valid from 'card-validator';
import sanitizeHtml from 'sanitize-html';
import { Html5Entities } from 'html-entities';
import { CustomFieldContract } from '../../types/CustomField';
import { SelectOption } from '../../types/common';

class UtilityService {
  public static validateCreditCardNumber(value: string): boolean {
    return valid.number(value).isValid;
  }

  public static getCreditCardType(value: string): string | undefined {
    return valid.number(value).card?.niceType;
  }

  public static getCookie(cname: string): string {
    const name = `${cname}=`;
    try {
      const decodedCookie = decodeURIComponent(document.cookie);
      const ca = decodedCookie.split(';');
      for (let i = 0; i < ca.length; i += 1) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
          return c.substring(name.length, c.length);
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      const matches = document.cookie.match(`${name}([^;]+);?`);
      if (matches && matches.length >= 2) {
        return matches[1];
      }
    }

    return '';
  }

  public static formatGiftCardCode = (code: string): string => {
    return code
      .split('')
      .reduce(
        (acc: string, cur: string) => acc.replace('x', cur),
        'xxxx-xxxxx-xxxx'
      );
  };

  public static formatAmount(
    amount: number,
    currencySymbol: string,
    additionalCost?: number
  ): string {
    const prefix = amount < 0 ? '-' : '';
    return `${prefix}${currencySymbol}${Math.abs(
      amount + (additionalCost ?? 0)
    ).toFixed(2)}`;
  }

  public static getBaseUrl(url: string): string {
    const matches = url.match(/^.+?[^/:](?=[?/]|$)/i); // run against regex
    return matches ? `${matches[0]}` : ''; // extract url without the subfolders or pages
  }

  public static getCartId(): string {
    let cartId = new URLSearchParams(`${window.location.search}/`).get(
      'cartId'
    );
    if (!cartId) {
      cartId = this.getCookie('CartID5');
    }
    return cartId;
  }

  public static sanitizeString(dirtyString: string): string {
    return sanitizeHtml(dirtyString, {
      allowedTags: [],
      allowedAttributes: {},
    });
  }

  public static sanitizeHtml(dirtyHtml: string): string {
    return sanitizeHtml(dirtyHtml, {
      allowedTags: [
        'b',
        'i',
        'em',
        'strong',
        'a',
        'br',
        'small',
        'sub',
        'sup',
        'mark',
      ],
      allowedAttributes: {
        a: ['href', 'target', 'rel'],
      },
      transformTags: {
        a: sanitizeHtml.simpleTransform('a', {
          target: '_blank',
          rel: 'noreferrer noopener',
        }),
      },
    });
  }

  public static convertHtmlListItemsToStringArray(htmlList: string): string[] {
    const messageList = htmlList
      .split('<li>')
      .reduce((messageElements: string[], element) => {
        const innerMessages = element.split('</li>');
        return messageElements.concat(innerMessages);
      }, []);
    return messageList.filter(message => message.trim());
  }

  public static convertHtmlErrorsToStringArray(htmlErrors: string[]): string[] {
    return htmlErrors.reduce((errorMessages: string[], htmlError) => {
      if (!htmlError.includes('<li>')) {
        errorMessages.push(htmlError);
        return errorMessages;
      }
      const messageStrings = htmlError.includes('<li>')
        ? UtilityService.convertHtmlListItemsToStringArray(htmlError)
        : [htmlError];
      return errorMessages.concat(messageStrings);
    }, []);
  }

  public static html5Decode(value: string): string {
    return Html5Entities.decode(value);
  }

  public static html5Encode(value: string | null | undefined): string {
    return Html5Entities.encode(value || '');
  }

  public static buildCustomFieldInputName(
    customField: CustomFieldContract
  ): string {
    return `${customField.tableName}_${customField.alias}`;
  }

  public static buildCustomFieldInputNameWithFormContext(
    customField: CustomFieldContract
  ): string {
    return `${customField.tableName}_${customField.alias}_${customField.id}`;
  }

  public static splitCustomFieldInputName(name: string): {
    tableName: string;
    alias: string;
  } {
    const splitName = name.split('_');

    const tableName = splitName[0];
    splitName.shift();
    const alias = splitName.join('_');

    return {
      tableName,
      alias,
    };
  }

  public static getMonthListAsSelectOptions(): SelectOption[] {
    const months = Array.from({ length: 12 }).map((_, index) =>
      (index + 1).toString().padStart(2, '0')
    );
    return UtilityService.getSelectOptionsFromArray(months);
  }

  public static getYearListAsSelectOptions(
    startYear = new Date().getFullYear()
  ): SelectOption[] {
    const years = Array.from({ length: 21 }).map((_, index) =>
      (startYear + index).toString()
    );
    return UtilityService.getSelectOptionsFromArray(years);
  }

  private static getSelectOptionsFromArray(list: string[]): SelectOption[] {
    return list.map(item => ({
      label: item,
      value: item,
    }));
  }
}

export default UtilityService;
